import { useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import Button from './ui/Button';
import Textarea from './ui/Textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import api from '../lib/api';

interface AIBriefGeneratorProps {
  corporateOrgId?: string;
  onApply: (brief: GeneratedBrief) => void;
}

export interface GeneratedBrief {
  title: string;
  description: string;
  exampleScript?: string;
  tagIds: string[];
  tagNames?: string[];
  targetDemographics?: {
    majorityRace?: 'malay' | 'chinese' | 'indian';
    sex?: 'male' | 'female';
    sexThreshold?: number;
    ageGroup?: 'age18_24' | 'age25_34' | 'age35_plus';
  } | null;
  totalBudgetRM?: number;
  estimatedKOL?: number;
  rewardsJson?: { cash: number; product: string; commission: number };
  products?: Array<{ title: string; description: string }>;
  suggestions?: string[];
}

export function AIBriefGenerator({ corporateOrgId: _corporateOrgId, onApply }: AIBriefGeneratorProps) {
  const [prompt, setPrompt] = useState('');
  const [productHint, setProductHint] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<GeneratedBrief | null>(null);

  const generate = async () => {
    if (prompt.trim().length < 10) {
      setError('Please describe your campaign in at least 10 characters');
      return;
    }

    setIsGenerating(true);
    setError(null);
    setPreview(null);

    try {
      const response = await api.post('/ai/jobs/draft-suggestion', {
        briefDescription: prompt.trim(),
        brandOrProduct: productHint.trim() || undefined,
      });
      const draft = response.data.draft || {};
      setPreview({
        title: draft.title || '',
        description: draft.description || '',
        exampleScript: draft.exampleScript,
        tagIds: draft.tagIds || [],
        tagNames: draft.suggestedTags || draft.tagNames,
        totalBudgetRM: draft.totalBudgetRM,
        estimatedKOL: draft.estimatedKOL,
        rewardsJson: draft.rewardsJson,
        products: draft.products,
        suggestions: draft.suggestions || draft.notes,
      });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate brief');
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card className="border-emerald-200 bg-emerald-50/50">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-emerald-600" />
          <CardTitle className="text-lg">AI Briefing Assistant</CardTitle>
        </div>
        <CardDescription>
          Describe your campaign in a few sentences and AI will generate a complete job brief
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-sm font-medium text-gray-700">Campaign Description</label>
          <Textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g., Nike wants a basketball campaign for NBA All-Star Weekend targeting young male sports fans on TikTok and Instagram..."
            className="mt-1 min-h-[100px]"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">Product Hint (optional)</label>
          <input
            type="text"
            value={productHint}
            onChange={(e) => setProductHint(e.target.value)}
            placeholder="e.g., Nike Basketball Collection"
            className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}

        {!preview && (
          <Button
            onClick={generate}
            disabled={isGenerating || prompt.trim().length < 10}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating Brief...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Generate Job Brief with AI
              </>
            )}
          </Button>
        )}

        {preview && (
          <div className="space-y-3 rounded-lg border bg-white p-4">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Generated Title</p>
              <p className="font-semibold text-gray-900">{preview.title}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Description</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap line-clamp-4">{preview.description}</p>
            </div>
            {preview.tagNames && preview.tagNames.length > 0 && (
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase">Suggested Tags</p>
                <p className="text-sm text-gray-700">{preview.tagNames.join(', ')}</p>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 text-sm">
              {preview.totalBudgetRM != null && (
                <div>
                  <span className="text-gray-500">Budget:</span> RM {preview.totalBudgetRM.toLocaleString()}
                </div>
              )}
              {preview.estimatedKOL != null && (
                <div>
                  <span className="text-gray-500">KOLs:</span> {preview.estimatedKOL}
                </div>
              )}
            </div>
            {preview.suggestions && preview.suggestions.length > 0 && (
              <ul className="text-xs text-gray-600 space-y-1">
                {preview.suggestions.map((s, i) => (
                  <li key={i} className="flex gap-1">
                    <span className="text-emerald-600">•</span> {s}
                  </li>
                ))}
              </ul>
            )}
            <div className="flex gap-2 pt-2">
              <Button onClick={() => onApply(preview)} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                Apply to Form
              </Button>
              <Button onClick={() => setPreview(null)} variant="outline" className="flex-1">
                Regenerate
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
